import { HttpClient } from '@angular/common/http';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { Endpoint, Network } from '@netfoundry-ui/shared/model';
import { ApiService, HTTP_CLIENT, LoggerService } from '@netfoundry-ui/shared/services';
import { FileUploader } from 'ng2-file-upload';
import { FileSaverService } from 'ngx-filesaver';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-bulk-upload-client-form',
    templateUrl: './bulk-upload-client-form.component.html',
    styleUrls: ['./bulk-upload-client-form.component.scss'],
    standalone: false
})
export class BulkUploadClientFormComponent implements OnInit, OnDestroy {
    @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
    @ViewChild('saveClientsAsCode', { static: true }) saveClientsAsCode;
    @ViewChild('showSummary', { static: true }) showSummary;

    @Input() isInline = false;
    @Input() public currentOrgId;
    @Input() public currentNetworkId;
    @Output() back: EventEmitter<boolean> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() newClients: EventEmitter<Endpoint[]> = new EventEmitter();
    step = 0;
    currentNetwork = new Network({});
    networkId;
    isLoading = false;
    // create an empty uploader until we have an actual URL
    uploader: FileUploader;
    fileUpload;
    form: FormGroup;
    fileIsReady = false;
    processing = false;
    hideHelp = false;
    businessKey;
    canListEndpoints = true;
    canCreateEndpoints = true;
    clients = [];
    sendEmails = false;
    cloneMessage = 'Want to add another environment<br />by uploading another csv file?';
    // boolean to determine if the window is attempting to hide
    private subscription = new Subscription();

    constructor(
        private logger: LoggerService,
        private renderer: Renderer2,
        private formBuilder: FormBuilder,
        private growlerService: GrowlerService,
        private apiService: ApiService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        private fileSaverService: FileSaverService,
        private dialogRef: MatDialogRef<BulkUploadClientFormComponent>,
        public authorizationService: AuthorizationService,
        public dialogForm: MatDialog
    ) {
        // initialize file upload form
        this.form = this.formBuilder.group({
            networkId: '',
            file: null,
        });

        // initialize the uploader for the drag and drop
        this.uploader = new FileUploader({ url: '' });
        this.canListEndpoints = this.authorizationService.canListEndpoints(undefined, undefined);
        this.canCreateEndpoints = this.authorizationService.canCreateEndpoints(undefined, undefined);
    }

    ngOnInit() {
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network) => {
                this.currentNetwork = new Network(network);
                this.networkId = this.currentNetwork.getId();

                // upload URL
                const uploadOptions = {
                    url: this.currentNetwork.getSelfLink() + '/endpoints?sendEmails=' + this.sendEmails,
                    authToken: 'Bearer ' + localStorage.getItem('access_token'),
                    additionalParameter: {
                        networkId: this.apiService.theNetworkIs.getId(),
                    },
                };
                this.uploader = new FileUploader(uploadOptions);
            })
        );
    }

    showDialog() {
        const event = new MouseEvent('click', { bubbles: true });
        this.fileInput.nativeElement.dispatchEvent(event);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    download() {
        const templateFile = 'ClientsTemplate.csv';
        this.http
            .get(`/assets/data/${templateFile}`, {
                observe: 'response',
                responseType: 'blob',
            })
            .subscribe((res) => {
                this.fileSaverService.save(res.body, templateFile);
            });
    }

    close() {
        this.dialogRef.close();
    }

    save() {
        this.logger.info('Saving Clients data...');
        this.addClientsCSV();
    }

    async addClientsCSV() {
        this.uploader.onBeforeUploadItem = (fileItem) => {
            fileItem.url = this.currentNetwork.getSelfLink() + '/endpoints?sendEmails=' + this.sendEmails;
        };

        // if we used the drag and drop
        if (this.uploader.queue.length > 0) {
            this.processing = true;
            const FileObject = this.uploader.queue[0];
            this.logger.info('Temp file', FileObject);
            this.logger.info('Adding file from queue...', FileObject._file);
            await FileObject.upload();
            this.fileUpload = FileObject;
            this.isLoading = true;
            this.step = 1;
            FileObject.onComplete = (response: any, status: any, headers: any) => {
                if (status < 300) {
                    const clientsAsCodeResponse = JSON.parse(response);
                    this.businessKey = clientsAsCodeResponse['businessKey'];
                    this.clients = clientsAsCodeResponse['endpoints'];
                    this.logger.info('clients as code upload process business key' + this.businessKey);
                    this.newClients.emit(this.clients);

                    this.saveClientsAsCode.fetchStatus();
                    this.growlerService.show(
                        new GrowlerData(
                            'success',
                            'Success',
                            'Clients Template Upload Complete',
                            'Clients template upload has been successful.'
                        )
                    );
                } else {
                    const error = JSON.parse(response);
                    this.logger.info('ERROR MESSAGE', error);
                    let errorMessage = '';
                    if (error != null && error[0] != null && error[0]['message'] != null) {
                        errorMessage = error[0]['message'];
                    }

                    this.growlerService.show(
                        new GrowlerData(
                            'error',
                            'Error',
                            'Clients Template Upload Failed',
                            'Clients template upload has failed. Import has been canceled. ' + errorMessage
                        )
                    );
                }
                this.isLoading = false;
                this.processing = false;
                this.uploader.clearQueue();
                if (status >= 300) {
                    this.resetForm();
                }
            };
        } else {
            this.logger.error('No files in queue to upload');
            this.growlerService.show(
                new GrowlerData('error', 'Error', 'Clients Template Upload Failed', 'No files queued to upload')
            );
        }
    }

    onFileChange(event) {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            this.logger.info('Adding file...', file);
            this.form.get('file').setValue(file);
            this.fileIsReady = true;
            this.uploader.addToQueue([this.form.get('file').value]);
        }
    }

    fileDrop() {
        if (this.uploader.queue.length > 0) {
            this.fileIsReady = true;
        }
    }

    // function for resetting the form
    resetForm() {
        this.businessKey = null;
        this.fileIsReady = false;
        this.processing = false;

        this.clients = [];

        this.sendEmails = false;
        this.fileInput.nativeElement.value = '';

        this.step = 0;
    }

    goBack() {
        this.back.emit(true);
    }

    hideForm(response?: unknown) {
        this.hide.emit(response);
    }

    toggleSendEmails() {
        this.sendEmails = !this.sendEmails;
    }
}
